// Error handling utilities for the application

export interface ApiError {
  message: string;
  type: string;
  status: number;
  fieldErrors?: Record<string, string>;
  details?: any;
}

export interface ErrorResponse {
  message: string;
  type: string;
  status: number;
  fieldErrors?: Record<string, string>;
  details?: any;
}

/**
 * Handles API errors and returns structured error information
 */
export const handleApiError = (error: any): ApiError => {
  console.error("API Error:", error);

  // Handle network errors
  if (!error.response) {
    return {
      message: "Network error. Please check your connection.",
      type: "NETWORK_ERROR",
      status: 0,
      details: error
    };
  }

  const status = error.response.status;
  const data = error.response.data;

  // Handle different HTTP status codes
  switch (status) {
    case 400:
      return {
        message: "Validation failed",
        type: "VALIDATION_ERROR",
        status: 400,
        fieldErrors: data,
        details: data
      };

    case 401:
      return {
        message: "Authentication required",
        type: "UNAUTHORIZED",
        status: 401,
        details: data
      };

    case 403:
      return {
        message: "You don't have permission to perform this action",
        type: "FORBIDDEN",
        status: 403,
        details: data
      };

    case 404:
      return {
        message: "Resource not found",
        type: "NOT_FOUND",
        status: 404,
        details: data
      };

    case 409:
      return {
        message: "Resource already exists",
        type: "CONFLICT",
        status: 409,
        details: data
      };

    case 422:
      return {
        message: "Invalid data provided",
        type: "UNPROCESSABLE_ENTITY",
        status: 422,
        fieldErrors: data,
        details: data
      };

    case 429:
      return {
        message: "Too many requests. Please try again later.",
        type: "RATE_LIMITED",
        status: 429,
        details: data
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        message: "Server error. Please try again later.",
        type: "SERVER_ERROR",
        status: status,
        details: data
      };

    default:
      return {
        message: data?.message || data?.detail || "An unexpected error occurred",
        type: "UNKNOWN_ERROR",
        status: status,
        details: data
      };
  }
};

/**
 * Formats error messages for display to users
 */
export const formatErrorMessage = (error: ApiError): string => {
  if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
    const fieldErrors = Object.values(error.fieldErrors).join(", ");
    return `${error.message}: ${fieldErrors}`;
  }
  return error.message;
};

export const formatErrorMessageForValidation = (error: ApiError): string => {
  if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
    const fieldErrors = Object.values(error.fieldErrors).join(", ");
    if (error.fieldErrors.message) {
      return error.fieldErrors.message;
    }
    return `${fieldErrors}`;
  }
  return error.message;
};

/**
 * Checks if an error is a validation error
 */
export const isValidationError = (error: ApiError): boolean => {
  return error.type === "VALIDATION_ERROR" || error.type === "UNPROCESSABLE_ENTITY";
};

/**
 * Checks if an error is a network error
 */
export const isNetworkError = (error: ApiError): boolean => {
  return error.type === "NETWORK_ERROR";
};

/**
 * Checks if an error is a server error
 */
export const isServerError = (error: ApiError): boolean => {
  return error.type === "SERVER_ERROR";
};

/**
 * Gets field-specific error messages
 */
export const getFieldErrors = (error: ApiError): Record<string, string> => {
  return error.fieldErrors || {};
};

/**
 * Logs error for debugging purposes
 */
export const logError = (error: ApiError, context?: string): void => {
  const logMessage = context ? `[${context}] ${error.message}` : error.message;
  console.error(logMessage, {
    type: error.type,
    status: error.status,
    fieldErrors: error.fieldErrors,
    details: error.details
  });
};
